import React, { useMemo, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../config/api'

const STATUS_LABEL = {
  matched: { label: 'Matched', cls: 'bg-green-100 text-green-800' },
  name_mismatch: { label: 'Name mismatch', cls: 'bg-amber-100 text-amber-800' },
  not_found: { label: 'Not found', cls: 'bg-red-100 text-red-800' },
  needs_resolution: { label: 'Needs resolution', cls: 'bg-orange-100 text-orange-800' },
  already_on_list: { label: 'Already on list', cls: 'bg-gray-100 text-gray-700' },
  invalid: { label: 'Invalid', cls: 'bg-gray-100 text-gray-500' },
}

function StatusBadge({ status }) {
  const meta = STATUS_LABEL[status] || { label: status, cls: 'bg-gray-100 text-gray-700' }
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${meta.cls}`}>{meta.label}</span>
}

/**
 * Watchlist CSV/XLSX bulk import — preview then commit.
 */
export default function WatchlistBulkImportModal({ open, onClose, onCommitted }) {
  const fileRef = useRef(null)
  const [fileName, setFileName] = useState('')
  const [loading, setLoading] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)
  const [rows, setRows] = useState([])
  const [includeNameMismatches, setIncludeNameMismatches] = useState(false)
  const [createVisitors, setCreateVisitors] = useState(false)
  const [selected, setSelected] = useState(() => new Set())
  const [candidatePicks, setCandidatePicks] = useState({})
  const [commitResult, setCommitResult] = useState(null)

  const reset = () => {
    setFileName('')
    setLoading(false)
    setCommitting(false)
    setError(null)
    setSummary(null)
    setRows([])
    setIncludeNameMismatches(false)
    setCreateVisitors(false)
    setSelected(new Set())
    setCandidatePicks({})
    setCommitResult(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    onClose?.()
  }

  const defaultSelectable = (row) => {
    if (row.status === 'matched') return true
    if (row.status === 'name_mismatch' && includeNameMismatches) return true
    if ((row.status === 'needs_resolution' || row.status === 'not_found') && createVisitors) return true
    if (row.status === 'needs_resolution' && candidatePicks[row.row_index]) return true
    return false
  }

  const selectableRows = useMemo(
    () => rows.filter((r) => defaultSelectable(r)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, includeNameMismatches, createVisitors, candidatePicks]
  )

  const runPreview = async (file) => {
    if (!file) return
    setLoading(true)
    setError(null)
    setCommitResult(null)
    setFileName(file.name)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await authFetch(getLocalApiUrl('/api/watchlist/bulk-import/preview'), {
        method: 'POST',
        body: fd,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      const previewRows = json.data?.rows || []
      setRows(previewRows)
      setSummary(json.data?.summary || null)
      const next = new Set()
      for (const r of previewRows) {
        if (r.status === 'matched') next.add(r.row_index)
      }
      setSelected(next)
    } catch (e) {
      setError(e?.message || 'Preview failed')
      setRows([])
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }

  const toggleRow = (rowIndex, on) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (on) next.add(rowIndex)
      else next.delete(rowIndex)
      return next
    })
  }

  const selectAllSelectable = () => {
    setSelected(new Set(selectableRows.map((r) => r.row_index)))
  }

  const commit = async () => {
    setCommitting(true)
    setError(null)
    try {
      const payloadRows = rows.map((r) => ({
        row_index: r.row_index,
        membership_number: r.membership_number,
        first_name: r.first_name,
        last_name: r.last_name,
        address: r.address,
        occupation: r.occupation,
        ranking: r.ranking,
        selected_master_profile_id: candidatePicks[r.row_index] || undefined,
      }))
      const res = await authFetch(getLocalApiUrl('/api/watchlist/bulk-import/commit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rows: payloadRows,
          row_indexes: [...selected],
          include_name_mismatches: includeNameMismatches,
          create_visitors: createVisitors,
          fill_missing_profile: true,
          recalculate_risk: true,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json.success) throw new Error(json.error || `HTTP ${res.status}`)
      setCommitResult(json.data)
      onCommitted?.(json.data)
    } catch (e) {
      setError(e?.message || 'Commit failed')
    } finally {
      setCommitting(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={handleClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-4xl bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="font-semibold text-gray-900">Import watchlist</div>
            <p className="text-xs text-gray-500 mt-0.5">
              Match on membership number; names verify; fill missing profile fields only; our risk score after import.
            </p>
          </div>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>

        <div className="p-6 space-y-4">
          <div className="border border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
            <label className="block text-sm font-medium text-gray-700 mb-2">CSV or Excel file</label>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls,.txt"
              className="block w-full text-sm text-gray-600"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) runPreview(f)
              }}
            />
            {fileName && <p className="text-xs text-gray-500 mt-2">Loaded: {fileName}</p>}
            <p className="text-xs text-gray-500 mt-2">
              Expected columns: Membership Number, Name, Surname, Address, Occupation, Initial Ranking
            </p>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
          )}

          {loading && <div className="text-sm text-gray-600">Parsing and matching…</div>}

          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              {Object.entries(summary).map(([k, v]) => (
                <div key={k} className="rounded border bg-white px-3 py-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</div>
                  <div className="font-semibold text-gray-900">{v}</div>
                </div>
              ))}
            </div>
          )}

          {rows.length > 0 && !commitResult && (
            <>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeNameMismatches}
                    onChange={(e) => setIncludeNameMismatches(e.target.checked)}
                  />
                  Include name mismatches
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createVisitors}
                    onChange={(e) => setCreateVisitors(e.target.checked)}
                  />
                  Create visitor profiles for unresolved / not found
                </label>
                <button type="button" className="text-primary-700 underline text-xs" onClick={selectAllSelectable}>
                  Select all eligible ({selectableRows.length})
                </button>
              </div>

              <div className="overflow-x-auto border rounded-md">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-2 py-2 text-left">Add</th>
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Status</th>
                      <th className="px-2 py-2 text-left">Membership</th>
                      <th className="px-2 py-2 text-left">Import name</th>
                      <th className="px-2 py-2 text-left">Profile</th>
                      <th className="px-2 py-2 text-left">Severity</th>
                      <th className="px-2 py-2 text-left">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((r) => {
                      const eligible = defaultSelectable(r)
                      const checked = selected.has(r.row_index)
                      return (
                        <tr key={r.row_index} className={eligible ? '' : 'bg-gray-50/60'}>
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              disabled={!eligible}
                              checked={checked && eligible}
                              onChange={(e) => toggleRow(r.row_index, e.target.checked)}
                            />
                          </td>
                          <td className="px-2 py-2 text-gray-500">{r.row_index}</td>
                          <td className="px-2 py-2">
                            <StatusBadge status={r.status} />
                          </td>
                          <td className="px-2 py-2 font-mono text-xs">{r.membership_number || '—'}</td>
                          <td className="px-2 py-2">
                            {[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
                          </td>
                          <td className="px-2 py-2">
                            {r.profile ? (
                              <span>
                                {[r.profile.first_name, r.profile.last_name].filter(Boolean).join(' ')}
                                {r.profile.membership_number ? (
                                  <span className="text-xs text-gray-500 ml-1">({r.profile.membership_number})</span>
                                ) : null}
                              </span>
                            ) : r.candidates?.length > 0 ? (
                              <select
                                className="border rounded px-1 py-0.5 text-xs max-w-[180px]"
                                value={candidatePicks[r.row_index] || ''}
                                onChange={(e) => {
                                  const v = e.target.value
                                  setCandidatePicks((prev) => ({ ...prev, [r.row_index]: v || undefined }))
                                  if (v) toggleRow(r.row_index, true)
                                }}
                              >
                                <option value="">Pick match…</option>
                                {r.candidates.map((c) => (
                                  <option key={c.master_profile_id} value={c.master_profile_id}>
                                    {[c.first_name, c.last_name].filter(Boolean).join(' ')}{' '}
                                    {c.membership_number || c.member_id || c.master_profile_id}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              '—'
                            )}
                            {r.fill_missing?.length > 0 && (
                              <div className="text-[11px] text-teal-700 mt-0.5">
                                Fill: {r.fill_missing.join(', ')}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2 capitalize">{r.suggested_severity}</td>
                          <td className="px-2 py-2 text-xs text-gray-600 max-w-xs">{r.message}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button onClick={commit} disabled={committing || selected.size === 0}>
                  {committing ? 'Importing…' : `Import ${selected.size} selected`}
                </Button>
              </div>
            </>
          )}

          {commitResult && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-2">
              <div className="font-medium text-green-900">Import complete</div>
              <div className="text-sm text-green-800">
                Committed {commitResult.summary?.committed ?? 0}, skipped {commitResult.summary?.skipped ?? 0},
                errors {commitResult.summary?.errors ?? 0}
              </div>
              {(commitResult.errors || []).length > 0 && (
                <ul className="text-xs text-red-700 list-disc pl-4">
                  {commitResult.errors.slice(0, 10).map((e) => (
                    <li key={`${e.row_index}-${e.error}`}>
                      Row {e.row_index}: {e.error}
                    </li>
                  ))}
                </ul>
              )}
              <div className="pt-2">
                <Button onClick={handleClose}>Done</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
